import React, { useState, useRef, useEffect } from 'react';
import { Box, TextField, IconButton, Typography, CircularProgress, Tooltip, ToggleButtonGroup, ToggleButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseFullscreenIcon from '@mui/icons-material/CloseFullscreen';
import { ReactComponent as SendArrowIcon } from '../../../assets/send-arrow.svg';
import { ReactComponent as CommentIcon } from '../../../assets/comment.svg';
import colors from 'assets/theme/base/colors';
import { genieStartConversation, genieGetMessage, genieSendFollowup } from '../../../service/service';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer } from 'recharts';
import { detectChartability, humanizeColumnName, computeNiceYAxisTicks, pivotForGroupedChart, detectStackedFromSql } from './chartability';

export interface GenieChatProps {
  onClose: () => void;
}

interface Attachment {
  text?: string | { content: string } | null;
  query?: {
    query: string;
    description: string;
    data?: {
      statement_response?: {
        manifest?: {
          schema?: {
            columns?: { name: string; position: number; type_name: string }[];
          };
        };
        result?: {
          data_array?: string[][];
        };
      };
    };
  } | null;
  // Genie-rendered visualization delivered as a base64 PNG by the backend
  // (feat/genie-viz-as-png). Rendered directly as an <img>, no iframe/login needed.
  viz?: {
    query_attachment_id?: string | null;
    data?: {
      image_base64?: string;
      content_type?: string;
    } | null;
  } | null;
}

// The Genie API returns `text` as a plain string in some responses and as
// `{ content: string }` in others (e.g. the raw payload) — normalize both.
function getAttachmentText(att: Attachment): string {
  if (!att.text) return '';
  return typeof att.text === 'string' ? att.text : att.text.content || '';
}

interface Message {
  id: string;
  role: 'user' | 'genie';
  content: string;
  attachments?: Attachment[];
  status?: 'loading' | 'completed' | 'failed';
}

// Genie returns follow-up suggestions in a few different shapes depending on
// the response: a top-level `suggested_questions` string array, or nested
// inside an attachment (`attachments[].suggested_questions.questions`) on
// either the simplified or raw Databricks payload — check them all.
function extractSuggestedQuestions(result: any): string[] {
  if (Array.isArray(result?.suggested_questions)) return result.suggested_questions;

  const candidateLists: any[] = [result?.attachments, result?.raw?.attachments];
  for (const list of candidateLists) {
    if (!Array.isArray(list)) continue;
    const match = list.find((att: any) => Array.isArray(att?.suggested_questions?.questions));
    if (match) return match.suggested_questions.questions;
  }
  return [];
}

const POLL_INTERVAL = 3000;
const POLL_TIMEOUT = 120000;
const EXPANDED_STORAGE_KEY = 'genie_chat_isExpanded';

const SUGGESTED_PROMPTS = [
  'What is the total 2027 Requested AOP?',
];

// ─────────────────────────────────────────────────────────────────────────────
// Lightweight markdown → HTML (mirrors PepWiseAIChatPopup's renderer)
// ─────────────────────────────────────────────────────────────────────────────

function renderMarkdownHTML(text: string): string {
  if (!text) return '';
  let html = text
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replace(/^### (.*$)/gim, '<h4 style="margin: 12px 0 6px 0; font-size: 14px; color: inherit;">$1</h4>')
    .replace(/^## (.*$)/gim, '<h3 style="margin: 14px 0 8px 0; font-size: 15px; color: inherit;">$1</h3>')
    .replace(/^# (.*$)/gim, '<h2 style="margin: 16px 0 10px 0; font-size: 16px; color: inherit;">$1</h2>')
    .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/gim, '<em>$1</em>')
    .replace(/`([^`]+)`/gim, '<code style="background: rgba(0,0,0,0.06); padding: 2px 4px; border-radius: 4px; font-family: monospace; font-size: 12px;">$1</code>')
    .replace(/^[ \t]{0,3}[-*•][ \t]{1,4}([^\n]+)/gim, '<li style="margin-left: 20px; list-style-type: disc; margin-bottom: 4px;">$1</li>')
    .replace(/^[ \t]{0,3}(\d+)\.[ \t]{1,4}([^\n]+)/gim, '<li style="margin-left: 20px; list-style-type: decimal; margin-bottom: 4px;">$2</li>')
    .replaceAll('---', '')
    .replace(/\n/gim, '<br />');

  html = html.replaceAll(/(<\/li>)(<br \/>)+/g, '$1');
  html = html.replaceAll(/(<\/h[234]>)(<br \/>)+/g, '$1');
  html = html.replaceAll(/(<br \/>)+(<h[234]>)/g, '$2');
  html = html.replaceAll(/(<br \/>){3,}/g, '<br /><br />');

  return html;
}

const PendingDots: React.FC = () => (
  <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.8, py: 0.5 }}>
    {[0, 1, 2].map((i) => (
      <Box
        key={i}
        component="span"
        sx={{
          width: 7, height: 7, borderRadius: '50%',
          backgroundColor: '#8CA3BE',
          animation: 'genieTyping 1.2s ease-in-out infinite',
          animationDelay: `${i * 0.18}s`,
          '@keyframes genieTyping': {
            '0%, 80%, 100%': { transform: 'scale(0.7)', opacity: 0.5 },
            '40%': { transform: 'scale(1)', opacity: 1 },
          },
        }}
      />
    ))}
  </Box>
);

const CHART_COLORS = ['#003771', '#0066CC', '#4A90D9', '#7CB5EC', '#A8D5F2', '#2E7D32', '#FF8F00', '#D32F2F', '#7B1FA2', '#00838F'];

function formatChartValue(val: number): string {
  if (Math.abs(val) >= 1e9) return `${(val / 1e9).toFixed(1)}B`;
  if (Math.abs(val) >= 1e6) return `${(val / 1e6).toFixed(1)}M`;
  if (Math.abs(val) >= 1e3) return `${(val / 1e3).toFixed(1)}K`;
  return val.toFixed(val % 1 === 0 ? 0 : 2);
}

const DataChart: React.FC<{ columns: { name: string }[]; rows: string[][]; sqlQuery?: string }> = ({ columns, rows, sqlQuery }) => {
  const { chartable, labelIdx, valueIndices, groupByIdx } = detectChartability(columns, rows);
  // Only bar charts for now -- line/pie are disabled while we work out chart
  // parity with Genie's own visualization (see docs/genie-chart-parity-cases.md).
  // detectChartability still computes a suggestedType; intentionally unused here.

  if (!chartable) return null;

  // Two shapes of input produce two shapes of series:
  // - normal: one series per numeric value column (valueIndices)
  // - grouped (a second categorical column, e.g. "Subcategory"): one series
  //   per distinct value of that column, pivoted out of long-format rows.
  const isGrouped = groupByIdx !== undefined;
  const { chartData, seriesKeys, valueColumnName } = isGrouped
    ? (() => {
        const { chartData: grouped, seriesNames } = pivotForGroupedChart(columns, rows, labelIdx, groupByIdx as number, valueIndices[0]);
        return { chartData: grouped, seriesKeys: seriesNames, valueColumnName: columns[valueIndices[0]].name };
      })()
    : {
        chartData: rows.map(row => {
          const entry: Record<string, string | number> = { label: row[labelIdx] || '' };
          valueIndices.forEach(vi => {
            entry[columns[vi].name] = Number(row[vi]) || 0;
          });
          return entry;
        }),
        seriesKeys: valueIndices.map(vi => columns[vi].name),
        valueColumnName: columns[valueIndices[0]].name,
      };

  // Stacked vs. clustered only matters for grouped (2-dimension) charts --
  // a single-series chart has nothing to stack. Signal: Genie's own SQL has
  // a GROUP BY when it intends the categorical columns to aggregate/compose
  // (see chartability.detectStackedFromSql and the 2026-09-15 Akash
  // meeting notes) -- a stronger signal than guessing from row counts.
  const isStacked = isGrouped && detectStackedFromSql(sqlQuery);

  const xAxisLabel = humanizeColumnName(columns[labelIdx].name);
  const yAxisLabel = humanizeColumnName(valueColumnName).toUpperCase();
  // Rotate X-axis tick labels only when there are enough categories that
  // horizontal labels would overlap -- Genie's own charts keep short label
  // sets (e.g. 2-3 categories) horizontal.
  const rotateXAxisTicks = chartData.length > 5;

  // Legend click-to-toggle is only meaningful with 2+ series -- with a
  // single series there's nothing to isolate, so the legend stays static
  // (no hover/click affordance) in that case.
  const canToggleSeries = seriesKeys.length > 1;
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());
  const toggleSeries = (key: string) => {
    if (!canToggleSeries) return;
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const visibleSeriesKeys = seriesKeys.filter(key => !hiddenSeries.has(key));

  // Round Y-axis ticks to "nice" steps (0/10/20/30) matching Genie's own
  // chart, instead of Recharts' default of scaling to the raw data max
  // (e.g. 0/8/16/24/32 for a max of 28.10). Recomputed from only the
  // visible series so hiding a series rescales the axis to fit what's left.
  // Stacked bars need the *sum* of visible series per category as the max
  // (bars pile up); clustered bars need the largest single series value.
  const rawMax = isStacked
    ? Math.max(0, ...chartData.map(entry => visibleSeriesKeys.reduce((sum, key) => sum + (Number(entry[key]) || 0), 0)))
    : Math.max(0, ...chartData.flatMap(entry => visibleSeriesKeys.map(key => Number(entry[key]) || 0)));
  const { ticks: yAxisTicks, domainMax: yAxisDomainMax } = computeNiceYAxisTicks(rawMax);

  return (
    <Box sx={{ mt: 1.5, mb: 1 }}>
      <Box sx={{ width: '100%', backgroundColor: '#FAFBFD', borderRadius: '8px', border: '1px solid #E2E8F0', p: 1 }}>
        <Box sx={{ width: '100%', height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 10, right: 20, bottom: rotateXAxisTicks ? 20 : 30, left: 30 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11 }}
                angle={rotateXAxisTicks ? -30 : 0}
                textAnchor={rotateXAxisTicks ? 'end' : 'middle'}
                height={rotateXAxisTicks ? 60 : 40}
                label={{ value: xAxisLabel, position: 'insideBottom', offset: rotateXAxisTicks ? -10 : -5, style: { fontSize: 12, fontWeight: 600, fill: '#1E293B' } }}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={formatChartValue}
                domain={[0, yAxisDomainMax]}
                ticks={yAxisTicks}
                label={{ value: yAxisLabel, angle: -90, position: 'insideLeft', style: { fontSize: 12, fontWeight: 600, fill: '#1E293B', textAnchor: 'middle' } }}
              />
              <RechartsTooltip formatter={(v: number) => formatChartValue(v)} />
              {seriesKeys.map((key, i) => (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={CHART_COLORS[i % CHART_COLORS.length]}
                  radius={isStacked ? undefined : [4, 4, 0, 0]}
                  hide={hiddenSeries.has(key)}
                  {...(isStacked ? { stackId: 'stack' } : {})}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 1 }}>
          {seriesKeys.map((key, i) => {
            const isHidden = hiddenSeries.has(key);
            return (
              <Box
                key={key}
                onClick={() => toggleSeries(key)}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.5,
                  cursor: canToggleSeries ? 'pointer' : 'default',
                  opacity: isHidden ? 0.4 : 1,
                  userSelect: 'none',
                  '&:hover': canToggleSeries ? { opacity: isHidden ? 0.6 : 0.8 } : undefined,
                }}
              >
                <Box sx={{ width: 10, height: 10, backgroundColor: CHART_COLORS[i % CHART_COLORS.length], flexShrink: 0 }} />
                {/* Grouped-chart series keys are data values (e.g. "AKS"), not
                    column names -- humanize only in the non-grouped case where
                    the key is a raw SQL column name like "allocation_amount". */}
                <Typography sx={{ fontSize: '11px', color: '#1E293B', textDecoration: isHidden ? 'line-through' : 'none' }}>
                  {isGrouped ? key : humanizeColumnName(key)}
                </Typography>
              </Box>
            );
          })}
        </Box>
      </Box>
    </Box>
  );
};

const DataTable: React.FC<{ columns: { name: string }[]; rows: string[][] }> = ({ columns, rows }) => {
  // Find columns where ALL values are null/empty and hide them
  const visibleColIndices = columns
    .map((_, idx) => idx)
    .filter((idx) => rows.some((row) => row[idx] != null && row[idx] !== '' && row[idx] !== 'null'));

  // Don't render if no visible columns remain
  if (visibleColIndices.length === 0) return null;

  return (
    <Box
    sx={{
      overflowX: 'auto',
      mt: 1,
      borderRadius: '8px',
      border: '1px solid #E2E8F0',
      scrollbarWidth: 'thin',
      '&::-webkit-scrollbar': { height: '5px' },
      '&::-webkit-scrollbar-thumb': { backgroundColor: '#CBD5E1', borderRadius: '4px' },
    }}
  >
    <Box
      component="table"
      sx={{
        width: '100%',
        borderCollapse: 'collapse',
        fontFamily: '"Plus Jakarta Sans", sans-serif',
        fontSize: '13px',
        '& th': {
          backgroundColor: '#003771',
          color: '#fff',
          fontWeight: 600,
          px: 1.5,
          py: 1,
          textAlign: 'left',
          whiteSpace: 'nowrap',
          borderBottom: '2px solid #002B59',
        },
        '& td': {
          px: 1.5,
          py: 0.75,
          borderBottom: '1px solid #E2E8F0',
          color: '#1E293B',
          whiteSpace: 'nowrap',
        },
        '& tr:nth-of-type(even) td': {
          backgroundColor: '#F8FAFC',
        },
        '& tr:hover td': {
          backgroundColor: '#EFF6FF',
        },
      }}
    >
      <thead>
        <tr>
          {visibleColIndices.map((idx) => (
            <th key={columns[idx].name}>{columns[idx].name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, rIdx) => (
          <tr key={`row-${row[0] || rIdx}`}>
            {visibleColIndices.map((idx) => (
              <td key={`${row[0]}-${columns[idx]?.name || idx}`}>{row[idx]}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </Box>
  </Box>
  );
};

const AttachmentBlock: React.FC<{ attachments: Attachment[] }> = ({ attachments }) => {
  // Small caption shown above each visualization so users can tell them apart
  // and understand how each was produced.
  const VizLabel: React.FC<{ title: string; subtitle: string }> = ({ title, subtitle }) => (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mt: 1.5, mb: 0.5 }}>
      <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#003771', opacity: 0.6, flexShrink: 0 }} />
      <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '11px', fontWeight: 700, color: '#003771', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
        {title}
      </Typography>
      <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '11px', fontWeight: 500, color: '#8CA3BE' }}>
        {subtitle}
      </Typography>
    </Box>
  );

  // Reorder: text-only attachments first, then table attachments
  const sorted = [...attachments].sort((a, b) => {
    const aHasTable = !!a.query?.data?.statement_response?.result?.data_array?.length;
    const bHasTable = !!b.query?.data?.statement_response?.result?.data_array?.length;
    if (aHasTable && !bHasTable) return 1;
    if (!aHasTable && bHasTable) return -1;
    return 0;
  });

  return (
    <>
      {sorted.map((att, attIdx) => {
        const textContent = getAttachmentText(att);
        const stmtResp = att.query?.data?.statement_response;
        const columns = stmtResp?.manifest?.schema?.columns;
        const rows = stmtResp?.result?.data_array;
        // >= 2 rows: enough to compare visually (bar/pie of 2+ categories).
        // A single row has nothing to compare against, so it stays text-only.
        const hasTable = columns && columns.length > 0 && rows && rows.length > 1;
        const vizImage = att.viz?.data?.image_base64;

        return (
          <Box key={`att-${textContent.slice(0, 15) || att.query?.query?.slice(0, 15) || att.viz?.query_attachment_id || attIdx}`} sx={{ mt: attIdx > 0 ? 1.5 : 0 }}>
            {textContent && (
              <Box
                component="div"
                sx={{ fontSize: '14px', lineHeight: '22px' }}
                dangerouslySetInnerHTML={{ __html: renderMarkdownHTML(textContent) }}
              />
            )}
            {hasTable && (
              <>
                <VizLabel title="Interactive chart" subtitle="Built from query results · switch bar / line / pie" />
                <DataChart columns={columns} rows={rows} sqlQuery={att.query?.query} />
                <VizLabel title="Data table" subtitle="Raw query results" />
                <DataTable columns={columns} rows={rows} />
              </>
            )}
            {vizImage && (
              <>
                <VizLabel title="Genie visualization" subtitle="Rendered by Databricks Genie" />
                <Box
                  component="img"
                  src={`data:${att.viz?.data?.content_type || 'image/png'};base64,${vizImage}`}
                  alt="Genie visualization"
                  sx={{
                    maxWidth: '100%',
                    display: 'block',
                    borderRadius: '8px',
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                  }}
                />
              </>
            )}
          </Box>
        );
      })}
    </>
  );
};

const MessageBubble: React.FC<{ msg: Message }> = ({ msg }) => {
  const isGenie = msg.role === 'genie';
  const isError = msg.status === 'failed';

  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, mb: 2, justifyContent: isGenie ? 'flex-start' : 'flex-end' }}>
      <Box
        sx={{
          maxWidth: 'min(85%, 800px)',
          px: 2.5,
          py: 1.5,
          borderRadius: isGenie ? '2px 20px 20px 20px' : '20px 20px 2px 20px',
          backgroundColor: isGenie ? '#FFFFFF' : '#003771',
          boxShadow: isGenie ? '0px 2px 8px rgba(0,0,0,0.05)' : 'none',
          border: isGenie ? '1px solid #E2E8F0' : 'none',
          borderLeft: isError ? '3px solid #E53E3E' : undefined,
        }}
      >
        <Typography
          component="div"
          sx={{
            color: isGenie ? '#1E293B' : '#FFFFFF',
            wordBreak: 'break-word',
            fontFamily: '"Plus Jakarta Sans", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '22px',
            letterSpacing: '-0.006em',
            '& h2, & h3, & h4': { color: isGenie ? '#003771' : '#FFFFFF', fontWeight: 700 },
            '& strong': { fontWeight: 700, color: isGenie ? '#0F172A' : '#FFFFFF' },
          }}
        >
          {msg.status === 'loading' ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PendingDots />
              <Typography component="span" sx={{ fontSize: '13px', color: '#8CA3BE' }}>Thinking...</Typography>
            </Box>
          ) : (
            <>
              {msg.content && (
                <Box component="div" sx={{ whiteSpace: 'pre-wrap' }}>{msg.content}</Box>
              )}
              {msg.attachments && <AttachmentBlock attachments={msg.attachments} />}
            </>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

const PromptChips: React.FC<{ prompts: string[]; onSelect: (p: string) => void; visible: boolean }> = ({ prompts, onSelect, visible }) => {
  const [dismissed, setDismissed] = useState(false);

  // Re-show panel whenever new chips arrive (new response)
  useEffect(() => { setDismissed(false); }, [prompts]);

  if (!visible || dismissed || prompts.length === 0) return null;

  return (
    <Box sx={{ mx: 2, mb: 1, backgroundColor: '#F0F5FB', border: '1px solid #D0DFF0', borderRadius: '12px', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, pt: 1, pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Box sx={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#003771', opacity: 0.6 }} />
          <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '11px', fontWeight: 600, color: '#5B7A9D', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
            Suggested follow-ups
          </Typography>
        </Box>
        <Box
          component="button"
          onClick={() => setDismissed(true)}
          sx={{ background: 'none', border: 'none', cursor: 'pointer', p: 0.25, color: '#94A3B8', display: 'flex', alignItems: 'center', '&:hover': { color: '#003771' } }}
        >
          <CloseIcon sx={{ fontSize: 13 }} />
        </Box>
      </Box>
      <Box sx={{ px: 1, pb: 1, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
        {prompts.map((p) => (
          <Box
            key={p}
            component="button"
            onClick={() => onSelect(p)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 1,
              width: '100%', textAlign: 'left',
              background: 'none', border: 'none', cursor: 'pointer',
              px: 1, py: 0.75, borderRadius: '8px',
              transition: 'background-color 0.15s',
              '&:hover': { backgroundColor: '#003771', '& .chip-text': { color: '#fff' }, '& .chip-icon': { color: '#fff' } },
            }}
          >
            <Box className="chip-icon" sx={{ color: '#003771', opacity: 0.5, flexShrink: 0, display: 'flex', alignItems: 'center' }}>
              <CommentIcon width={12} height={12} />
            </Box>
            <Typography className="chip-text" sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '13px', fontWeight: 500, color: '#1E3A5F', lineHeight: '18px' }}>
              {p}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const ChatInputBar: React.FC<{ value: string; onChange: (v: string) => void; onSend: () => void; isLoading?: boolean }> = ({ value, onChange, onSend, isLoading = false }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', px: '16px', py: '16px', backgroundColor: 'transparent' }}>
    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', backgroundColor: '#FFFFFF', borderRadius: '50px', boxShadow: '0px 4px 4px rgba(0,0,0,0.25)', border: '1px solid #E2E2E2', height: '60px', pl: '24px', pr: '6px', gap: 2 }}>
      <TextField
        fullWidth
        variant="standard"
        placeholder="Ask a question about your FinOps data..."
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (!isLoading) onSend();
          }
        }}
        disabled={isLoading}
        InputProps={{ disableUnderline: true }}
        sx={{ '& input': { fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '14px', fontWeight: 500, color: '#1E293B', '&::placeholder': { color: '#94A3B8', opacity: 1 } } }}
      />
      <Box
        component="button"
        onClick={onSend}
        disabled={isLoading || !value.trim()}
        sx={{ backgroundColor: '#003771', color: '#fff', width: '48px', height: '48px', borderRadius: '50%', border: 'none', flexShrink: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 0, outline: 'none', transition: 'background-color 0.2s', '&:hover': { backgroundColor: '#002B59' }, '&:disabled': { backgroundColor: '#7A9BB8', cursor: 'not-allowed' } }}
      >
        {isLoading ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <SendArrowIcon width={22} height={20} />}
      </Box>
    </Box>
  </Box>
);

const GenieChat: React.FC<GenieChatProps> = ({ onClose }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState<boolean>(() => sessionStorage.getItem(EXPANDED_STORAGE_KEY) === 'true');
  const [viewMode, setViewMode] = useState<'chat' | 'visual'>('chat');
  // Once the user opens Visual mode we keep the iframe mounted (just hidden in
  // Chat mode) so the Genie conversation thread is preserved when toggling back.
  const [visualEverOpened, setVisualEverOpened] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Drag-to-move (compact mode only). `dragPos` null = use default CSS anchor.
  const panelRef = useRef<HTMLDivElement>(null);
  const [dragPos, setDragPos] = useState<{ top: number; left: number } | null>(null);
  const dragState = useRef({ dragging: false, offsetX: 0, offsetY: 0 });

  useEffect(() => {
    if (viewMode === 'visual') setVisualEverOpened(true);
  }, [viewMode]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleDragStart = (e: React.MouseEvent) => {
    // Don't drag in full-screen mode, or when interacting with buttons/toggles.
    if (isExpanded) return;
    if ((e.target as HTMLElement).closest('button')) return;
    const rect = panelRef.current?.getBoundingClientRect();
    if (!rect) return;
    dragState.current = { dragging: true, offsetX: e.clientX - rect.left, offsetY: e.clientY - rect.top };
    setDragPos({ top: rect.top, left: rect.left });
    e.preventDefault();
  };

  useEffect(() => {
    const handleMove = (e: MouseEvent) => {
      if (!dragState.current.dragging) return;
      const panelW = panelRef.current?.offsetWidth ?? 597;
      const panelH = panelRef.current?.offsetHeight ?? 600;
      const newLeft = Math.max(0, Math.min(e.clientX - dragState.current.offsetX, window.innerWidth - panelW));
      const newTop = Math.max(0, Math.min(e.clientY - dragState.current.offsetY, window.innerHeight - panelH));
      setDragPos({ top: newTop, left: newLeft });
    };
    const handleUp = () => { dragState.current.dragging = false; };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, []);

  const extractData = (resp: any) => {
    // Handle nested response: { data: { ...actualData } } from APIsuccess wrapper
    if (resp?.data?.data) return resp.data.data;
    if (resp?.data) return resp.data;
    return resp;
  };

  const pollForResult = async (convId: string, msgId: string): Promise<any> => {
    const start = Date.now();
    while (Date.now() - start < POLL_TIMEOUT) {
      const resp = await genieGetMessage(convId, msgId);
      const data = extractData(resp);
      const status = data?.status;

      if (status === 'COMPLETED') return data;
      if (status === 'FAILED' || status === 'CANCELLED') return data;

      await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL));
    }
    return { status: 'FAILED', error: 'Timeout waiting for response' };
  };

  const handleSend = async (overrideText?: string) => {
    const question = (overrideText ?? input).trim();
    if (!question || isLoading) return;

    setInput('');
    const userMsgId = `user-${Date.now()}`;
    const genieMsgId = `genie-${Date.now()}`;
    setMessages((prev) => [...prev, { id: userMsgId, role: 'user', content: question }]);
    setMessages((prev) => [...prev, { id: genieMsgId, role: 'genie', content: '', status: 'loading' }]);
    setIsLoading(true);

    try {
      let convId = conversationId;
      let msgId: string;

      if (!convId) {
        const resp = await genieStartConversation(question);
        const data = extractData(resp);
        convId = data.conversation_id;
        msgId = data.message_id;
        setConversationId(convId);
      } else {
        const resp = await genieSendFollowup(convId, question);
        const data = extractData(resp);
        msgId = data.message_id;
      }

      const result = await pollForResult(convId!, msgId!);
      const attachments: Attachment[] = result?.attachments || [];

      // Build response content
      let responseContent = '';
      if (result?.status === 'FAILED') {
        const errMsg = result?.errors?.error || result?.error?.message || result?.error || '';
        responseContent = errMsg || 'I could not process that question. Please try rephrasing.';
      } else if (attachments.length === 0) {
        responseContent = 'No results found.';
      }

      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          id: genieMsgId,
          role: 'genie',
          content: responseContent,
          attachments: attachments.length > 0 ? attachments : undefined,
          status: result?.status === 'COMPLETED' ? 'completed' : 'failed',
        };
        return updated;
      });
    } catch (err: any) {
      console.error('Genie chat error:', err?.message || err);
      setMessages((prev) => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          id: genieMsgId,
          role: 'genie',
          content: 'Something went wrong. Please try again.',
          status: 'failed',
        };
        return updated;
      });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleExpanded = (val: boolean) => {
    setIsExpanded(val);
    setDragPos(null); // reset any dragged position when switching layout
    sessionStorage.setItem(EXPANDED_STORAGE_KEY, String(val));
  };

  const handleNewConversation = () => {
    setMessages([]);
    setConversationId(null);
    setInput('');
    setIsLoading(false);
  };

  const headerIconBtnSx = {
    color: '#fff', p: '4px',
    '&:hover': { backgroundColor: 'rgba(255,255,255,0.15)' },
  };

  const scrollbarSx = {
    scrollbarWidth: 'thin',
    scrollbarColor: '#CBD5E1 transparent',
    '&::-webkit-scrollbar': { width: '5px' },
    '&::-webkit-scrollbar-thumb': { backgroundColor: '#CBD5E1 !important', borderRadius: '4px' },
    '&::-webkit-scrollbar-track': { backgroundColor: 'transparent !important' },
  };

  const compactSize = { width: '597px', height: '80vh', minHeight: '400px', maxHeight: '915px', borderRadius: '10px' };
  let panelPositionSx: Record<string, any>;
  if (isExpanded) {
    panelPositionSx = { top: '40px', left: '60px', right: 0, bottom: 0, width: 'auto', height: 'auto', maxHeight: 'none', minHeight: 'auto', borderRadius: 0 };
  } else if (dragPos) {
    panelPositionSx = { top: `${dragPos.top}px`, left: `${dragPos.left}px`, right: 'auto', bottom: 'auto', ...compactSize };
  } else {
    panelPositionSx = { bottom: '76px', right: '20px', ...compactSize };
  }

  return (
    <Box
      ref={panelRef}
      sx={{
        position: 'fixed',
        ...panelPositionSx,
        backgroundColor: colors.backgroundLight.secondary,
        boxShadow: isExpanded ? '0 0 40px rgba(0,0,0,0.18)' : `0 8px 36px rgba(0,0,0,0.2), 0 0 0 2px ${colors.landingPageTile.background}`,
        display: 'flex',
        flexDirection: 'column',
        zIndex: 1200,
        overflow: 'hidden',
        animation: 'genieChatSlideUp 0.22s ease-out',
        '@keyframes genieChatSlideUp': {
          from: { opacity: 0, transform: 'translateY(16px) scale(0.96)' },
          to: { opacity: 1, transform: 'translateY(0) scale(1)' },
        },
      }}
    >
      {/* Header */}
      <Box
        onMouseDown={handleDragStart}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.25, backgroundColor: colors.landingPageTile.background, flexShrink: 0, cursor: isExpanded ? 'default' : 'move', userSelect: 'none' }}
      >
        <Box>
          <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '14px', fontWeight: 600, color: '#fff', lineHeight: 1 }}>
            FinOps AI Assistant
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ToggleButtonGroup value={viewMode} exclusive onChange={(_, v) => v && setViewMode(v)} size="small" sx={{ backgroundColor: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)' }}>
            <ToggleButton value="chat" sx={{ color: '#fff', fontSize: '11px', px: 1, border: 'none !important', '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.3)', color: '#fff' } }}>Chat</ToggleButton>
            <ToggleButton value="visual" sx={{ color: '#fff', fontSize: '11px', px: 1, border: 'none !important', '&.Mui-selected': { backgroundColor: 'rgba(255,255,255,0.3)', color: '#fff' } }}>Visual</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Box sx={{ display: 'flex', gap: '2px' }}>
          {messages.length > 0 && (
            <Tooltip title="New Chat">
              <IconButton size="small" onClick={handleNewConversation} sx={headerIconBtnSx} disabled={isLoading}>
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={isExpanded ? 'Compact view' : 'Expand'}>
            <IconButton size="small" onClick={() => toggleExpanded(!isExpanded)} sx={headerIconBtnSx}>
              {isExpanded ? <CloseFullscreenIcon sx={{ fontSize: 15 }} /> : <OpenInFullIcon sx={{ fontSize: 15 }} />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={onClose} sx={headerIconBtnSx}>
              <CloseIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Messages or Visual */}
      {viewMode === 'chat' && (
        <Box sx={{ flex: 1, overflowY: 'auto', p: 2, display: 'flex', flexDirection: 'column', ...scrollbarSx }}>
          {messages.length === 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', flex: 1, textAlign: 'center' }}>
              <Typography sx={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '17px', fontWeight: 600, color: '#003771' }}>
                What would you like to know?
              </Typography>
            </Box>
          )}
          {messages.map((msg) => (
            <MessageBubble key={msg.id} msg={msg} />
          ))}
          <div ref={messagesEndRef} />
        </Box>
      )}
      {/* Visual iframe stays mounted once opened so the Genie thread persists
          when toggling back and forth; only its visibility is switched. */}
      {visualEverOpened && (
        <Box sx={{ flex: 1, display: viewMode === 'visual' ? 'flex' : 'none', flexDirection: 'column', overflow: 'hidden', backgroundColor: '#F8FAFC' }}>
          <Box
            component="iframe"
            src="https://adb-886238043139051.11.azuredatabricks.net/embed/genie/rooms/01f190af9e9f1da69ee48fcf71832ce4?o=886238043139051"
            allow="clipboard-write; storage-access"
            sx={{
              flex: 1,
              border: 'none',
              width: '100%',
            }}
          />
        </Box>
      )}

      {viewMode === 'chat' && <ChatInputBar value={input} onChange={setInput} onSend={() => handleSend()} isLoading={isLoading} />}
    </Box>
  );
};

export default GenieChat;
